import { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return iso.slice(11, 19);
  }
};

const fmtLatency = (ms) => {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

/**
 * Tool History Drawer — last 20 tool invocations.
 *
 * Reads /api/tiny-tim/tool-history. Lets Nick spot a flaky tool (e.g.
 * fatigue_detection erroring 4 of last 5 calls) before Tim silently routes
 * around it.
 */
const TinyTimToolHistory = ({ limit = 20 }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/tiny-tim/tool-history`, { params: { limit } });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load tool history');
      }
    };
    fetchData();
    const t = setInterval(fetchData, 30000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [limit]);

  if (error) {
    return (
      <div
        data-testid="tiny-tim-tool-history-error"
        className="rounded-xl border border-red-900/40 bg-red-950/20 p-4 text-sm text-red-300"
      >
        Tool history unavailable: {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div
        data-testid="tiny-tim-tool-history-loading"
        className="rounded-xl border border-slate-800 bg-slate-950/40 p-4 text-sm text-slate-500"
      >
        Loading tool history…
      </div>
    );
  }

  const rows = data.rows || [];
  const errCount = rows.filter((r) => r.tool_ok === false).length;

  return (
    <div
      data-testid="tiny-tim-tool-history"
      className="rounded-xl border border-slate-800 bg-slate-950/40 p-4 flex flex-col"
    >
      <div className="flex items-baseline justify-between mb-2">
        <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
          Tool history · last {data.count}
        </p>
        <span
          data-testid="tiny-tim-tool-history-error-count"
          className={`text-[11px] ${errCount > 0 ? 'text-amber-400' : 'text-slate-500'}`}
        >
          {errCount} error{errCount === 1 ? '' : 's'}
        </span>
      </div>
      {rows.length === 0 ? (
        <p data-testid="tiny-tim-tool-history-empty" className="text-sm text-slate-500 py-4">
          No tools have run yet.
        </p>
      ) : (
        <ul className="space-y-1 max-h-72 overflow-y-auto pr-1">
          {rows.map((r, i) => {
            const okColor = r.tool_ok === false ? 'bg-red-400' : 'bg-emerald-400';
            return (
              <li
                key={`${r.created_at}-${i}`}
                data-testid={`tt-tool-row-${i}`}
                className="flex items-center gap-2 text-xs py-1 border-b border-slate-900/60 last:border-b-0"
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${okColor} shrink-0`}
                  aria-label={r.tool_ok === false ? 'error' : 'ok'}
                />
                <span className="text-slate-500 font-mono w-16 shrink-0">{fmtTime(r.created_at)}</span>
                <span className="text-slate-200 font-mono truncate max-w-[12rem]">{r.tool_name || '—'}</span>
                <span
                  className={`text-[10px] font-mono ml-auto shrink-0 ${
                    r.tool_ok === false ? 'text-red-400' : 'text-slate-500'
                  }`}
                >
                  {fmtLatency(r.latency_ms)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <p className="mt-2 text-[10px] text-slate-600">
        Updates every 30s. Red dot = tool error.
      </p>
    </div>
  );
};

export default TinyTimToolHistory;
